import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuTrigger } from '@coreai/fluentui-react';
import { Add16Regular, ChevronDown12Regular } from '@fluentui/react-icons';
import { paths } from '@/routes';
import { usePillStyles } from './pillStyles';
import { useMenuList } from './useMenuList';
import { PillMenu } from './PillMenu';
import type { AutomationAppUIModel } from '@/types/automation';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { blockHeaderActionForCodeIssue } from './codeNavigationGuard';

interface AppPillProps {
  /** Current location (Azure region) name from URL params. */
  locationName: string;
  /** Current project name from URL params. */
  projectName: string;
  /** Current app name from URL params. */
  appName: string;
  /** Apps in the current project. */
  apps: readonly AutomationAppUIModel[];
  /** Tenant id for navigation. */
  tenantId?: string | null | undefined;
  /** When true, the user is already on the app landing route, so the
   *  "Go to app" affordance on the current row is suppressed. */
  isAtAppRoot?: boolean;
  /** When provided (and `canCreate` is true), the dropdown renders a
   *  "+ Create new app" footer that invokes this callback. */
  onCreate?: () => void;
  /** Gates the create footer for users without the right permission. Defaults
   *  to true; pass false to hide the footer even when `onCreate` is supplied. */
  canCreate?: boolean;
  /** Telemetry callback fired when the user selects an app. */
  onAppChange?: (appName: string) => void;
}

/**
 * Neutral pill for the application level.
 *
 * Switching the app drops workflow context (lands on the app's workflows list)
 * to mirror the established navigation contract.
 */
export const AppPill = ({
  locationName,
  projectName,
  appName,
  apps,
  tenantId,
  isAtAppRoot,
  onCreate,
  canCreate = true,
  onAppChange,
}: AppPillProps) => {
  const styles = usePillStyles();
  const navigate = useNavigate();
  const requestNodeCodeNavigation = useNodeEditorStore(
    (state) => state.requestNodeCodeNavigation,
  );

  const getAppName = useCallback((a: AutomationAppUIModel) => a.name, []);
  const isCurrentApp = useCallback(
    (app: AutomationAppUIModel) => app.name === appName,
    [appName],
  );
  const {
    search,
    setSearch,
    onMenuOpenChange,
    visible: visibleApps,
    isSearching,
  } = useMenuList(apps, getAppName, isCurrentApp);

  const handleAppClick = useCallback(
    (app: AutomationAppUIModel) => {
      // Clicking the current app no longer no-ops: it acts as the
      // "Go to app" affordance and navigates to the app's workflows list.
      const destination = paths.appWorkflows(
        locationName,
        projectName,
        app.name,
        tenantId,
      );
      if (blockHeaderActionForCodeIssue()) return;
      if (requestNodeCodeNavigation(destination)) return;
      if (app.name !== appName) onAppChange?.(app.name);
      navigate(destination);
    },
    [
      appName,
      locationName,
      projectName,
      tenantId,
      navigate,
      onAppChange,
      requestNodeCodeNavigation,
    ],
  );
  const handleCreate = useCallback(() => {
    if (
      blockHeaderActionForCodeIssue({
        includeNodeCode: true,
        title: 'Apply code changes before creating an app',
      })
    ) return;
    onCreate?.();
  }, [onCreate]);

  return (
    <Menu onOpenChange={onMenuOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.appPill}
          aria-label={`Application: ${appName}. Click to switch.`}
        >
          <span className={styles.pillPrefix} aria-hidden="true">App:</span>
          <span className={styles.pillLabel}>{appName}</span>
          <ChevronDown12Regular className={styles.pillChevron} aria-hidden="true" />
        </button>
      </MenuTrigger>
      <PillMenu
        sectionTitle="Apps"
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search apps…"
        searchAriaLabel="Search apps"
        items={visibleApps}
        getKey={getAppName}
        getLabel={getAppName}
        isCurrent={isCurrentApp}
        onSelect={handleAppClick}
        isSearching={isSearching}
        emptyCopy="No apps in this environment"
        noMatchCopy="No matching apps"
        {...(isAtAppRoot ? {} : { currentItemActionLabel: 'Go to app' })}
        {...(canCreate && onCreate
          ? {
              viewAllLabel: 'Create new app',
              viewAllIcon: <Add16Regular aria-hidden="true" />,
              onViewAll: handleCreate,
            }
          : {})}
      />
    </Menu>
  );
};
